import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../types';
import { AdvancedFilterValue } from '../../models/advancedFilters';

export interface ViewLabelFilterState {
  view: AdvancedFilterValue | undefined;
  features: AdvancedFilterValue[];
  advancedFilters: {
    datasources: AdvancedFilterValue;
    dicomICID: string;
    study: string;
    viewLabelICID: string; // Changed from keypointICID
    annotationSetName?: string;
    technician: AdvancedFilterValue[];
    created_at_after: string | undefined;
    created_at_before: string | undefined;
    updated_at_after: string | undefined;
    updated_at_before: string | undefined;
  };
  activeTags: AdvancedFilterValue[];
  shouldFetchViewLabels: boolean; // Added new field
  page: number;
  size: number;
}

const initialState: ViewLabelFilterState = {
  view: undefined,
  features: [],
  advancedFilters: {
    datasources: { uuid: '', name: '' },
    dicomICID: '',
    study: '',
    viewLabelICID: '',
    technician: [],
    created_at_after: undefined,
    created_at_before: undefined,
    updated_at_after: undefined,
    updated_at_before: undefined,
  },
  activeTags: [],
  shouldFetchViewLabels: false, // Initial value for the new field
  page: 1,
  size: 6,
};

export const viewLabelFilterStateSlice = createSlice({
  name: 'viewLabelFilterState',
  initialState,
  reducers: {
    setView: (state, action: PayloadAction<AdvancedFilterValue | undefined>) => {
      state.view = action.payload;
      if (state.view?.uuid !== action.payload?.uuid) {
        state.features = [];
      }
    },
    setFeatures: (state, action: PayloadAction<AdvancedFilterValue[]>) => {
      state.features = action.payload;
    },
    setAdvancedFilter: (
      state,
      action: PayloadAction<{
        key: string;
        value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined;
      }>
    ) => {
      const { key, value } = action.payload;

      // Deep comparison for objects and arrays
      const areValuesEqual = (val1: any, val2: any): boolean => {
        if (val1 === val2) return true;
        if (
          typeof val1 !== 'object' ||
          val1 === null ||
          typeof val2 !== 'object' ||
          val2 === null
        ) {
          return false;
        }
        if (Array.isArray(val1) && Array.isArray(val2)) {
          if (val1.length !== val2.length) return false;
          const sortedVal1 = [...val1].sort((a, b) => (a.uuid || '').localeCompare(b.uuid || ''));
          const sortedVal2 = [...val2].sort((a, b) => (a.uuid || '').localeCompare(b.uuid || ''));
          return sortedVal1.every((item, index) => areValuesEqual(item, sortedVal2[index]));
        }
        if (typeof val1 === 'object' && typeof val2 === 'object') {
          const keys1 = Object.keys(val1);
          const keys2 = Object.keys(val2);
          if (keys1.length !== keys2.length) return false;
          for (const k of keys1) {
            if (!keys2.includes(k) || !areValuesEqual(val1[k], val2[k])) {
              return false;
            }
          }
          return true;
        }
        return false;
      };

      if (!areValuesEqual((state.advancedFilters as any)[key], value)) {
        state.advancedFilters = {
          ...state.advancedFilters,
          [key]: value,
        } as any;
      }
    },
    resetFilters: state => {
      state.view = initialState.view;
      state.features = initialState.features;
      state.advancedFilters = initialState.advancedFilters;
      state.activeTags = initialState.activeTags;
    },
    updateActiveTags: (state, action: PayloadAction<AdvancedFilterValue[]>) => {
      state.activeTags = action.payload;
    },
    removeFilterTag: (state, action: PayloadAction<AdvancedFilterValue>) => {
      const tag = action.payload;

      state.features = state.features.filter(feature => feature.uuid !== tag.uuid);

      if (state.view?.uuid === tag.uuid) {
        state.view = undefined;
      }

      Object.keys(state.advancedFilters).forEach(key => {
        const filterKey = key as keyof typeof state.advancedFilters;
        const filterValue = state.advancedFilters[filterKey] as any;

        if (Array.isArray(filterValue)) {
          const isStringArray = filterValue.length === 0 || typeof filterValue[0] === 'string';
          if (isStringArray) {
            (state.advancedFilters as any)[filterKey] = filterValue.filter((item: any) => {
              const itemUuid = `${filterKey}_${item}`;
              return itemUuid !== tag.uuid && item !== tag.uuid;
            });
          } else {
            (state.advancedFilters as any)[filterKey] = filterValue.filter(
              (item: any) => item?.uuid !== tag.uuid
            );
          }
        } else if (typeof filterValue === 'object' && filterValue !== null) {
          if ((filterValue as any).uuid === tag.uuid) {
            (state.advancedFilters as any)[filterKey] = { uuid: '', name: '' };
          }
        } else if (typeof filterValue === 'string') {
          if (tag.name.includes(filterValue) && filterValue !== '') {
            (state.advancedFilters as any)[filterKey] = '';
          }
        }
      });

      state.activeTags = state.activeTags.filter(t => t.uuid !== tag.uuid);
    },
    setShouldFetchViewLabels: (state, action: PayloadAction<boolean>) => {
      state.shouldFetchViewLabels = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setSize: (state, action: PayloadAction<number>) => {
      state.size = action.payload;
    },
  },
});

export const {
  setView,
  setFeatures,
  setAdvancedFilter,
  resetFilters,
  updateActiveTags,
  removeFilterTag,
  setShouldFetchViewLabels,
  setPage,
  setSize,
} = viewLabelFilterStateSlice.actions;

export const selectViewLabelFilterState = (state: RootState) => (state as any).viewLabelFilterState;
export const selectViewLabelActiveFilters = (state: RootState) =>
  (state as any).viewLabelFilterState.activeTags;
export default viewLabelFilterStateSlice.reducer;
